import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import styled from "styled-components";
import { useHistory } from "react-router";
import axios from "axios";
import Loader from "react-loader-spinner";
import { LoaderWrapper } from "../../components/styles/Loader";
import { Button } from "../../components/styles/Button";
import { Container } from "../../components/styles/Layout";

import useSeo from "../../hooks/useSeo";
import useJsonLd from "../../hooks/useJsonLd";
import { postSeo, SITE_NAME, ORIGIN } from "../../data/seo";
import { postSchema } from "../../data/structuredData";
import { postPath, extractPostId } from "../../data/postSlug";
import { extractYouTubeId, youtubeEmbedUrl } from "../../data/youtubeVideo";

const ArticleWrap = styled.div`
    margin: 3rem 0 1rem 0;
    max-width: 50rem;
    & div {
        line-height: 1.5rem;
    }

    /*
     * Call-to-action buttons written into post.content, matching the sales
     * guide's .btn.royal / .btn.outline (guideStyles.css, which only loads on
     * the guide pages). The post carries its own inline styles so it still
     * looks right without this - hence !important on the hover colours,
     * which would otherwise lose to the inline background and colour.
     */
    & a.pp-btn {
        transition: transform 0.2s, background 0.2s, color 0.2s;
    }
    & a.pp-btn:hover {
        text-decoration: none;
    }
    & a.pp-btn.royal:hover {
        background: #0052cc !important;
        transform: translateY(-2px);
    }
    & a.pp-btn.outline:hover {
        background: #fff !important;
        color: #112b4e !important;
    }
    @media (prefers-reduced-motion: reduce) {
        & a.pp-btn {
            transition: none;
        }
    }
`;
/*
 * The article title was an h2, so blog posts shipped with no h1 either.
 * Pins the previous h2 rendering (31.5px / 200, 1.5 line-height, 0.83em
 * margins) so only the semantics change - index.css would otherwise render
 * an h1 at the browser default 2em with letter-spacing: 1px.
 */
const Title = styled.h1`
    font-size: 1.75rem;
    font-weight: 200;
    line-height: 1.5;
    letter-spacing: normal;
    margin: 0.83em 0;
`;
const FeaturedImage = styled.img`
    width: 100%;
    max-height: 26rem;
    object-fit: cover;
    display: block;
    margin-bottom: 1.5rem;
`;

/*
 * Padding-top 56.25% is the standard responsive-iframe trick for a fixed
 * 16:9 embed - percentage padding on a block element is relative to its own
 * WIDTH, so this reserves exactly the right height at any container size
 * without JavaScript or a fixed pixel height that would crop on mobile.
 */
const VideoWrap = styled.div`
    position: relative;
    width: 100%;
    padding-top: 56.25%;
    margin-bottom: 1.5rem;
    background: #000;
`;

const VideoEmbed = styled.iframe`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
`;

const Body = styled.div``;

const BackButton = styled(Button)`
    max-width: 20rem;
    margin-top: 2rem;
`;

export default function SingleBlog() {
    const [post, setPost] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    const location = useLocation();

    /*
     * The route segment is now `<slug>-<id>` for a link this site generates
     * itself, but a bare id or a stray value still reaches here - an old
     * bookmark, or a legacy title-only slug that api/render.js could not
     * match and so left for the app to handle. extractPostId pulls the real
     * Mongo id out of any of those; null means there is nothing to look up,
     * same as SingleProperty.js.
     */
    const path = extractPostId(location.pathname.split("/")[2]);

    const history = useHistory();

    // Title, description and canonical from the post's own title and
    // excerpt. `post` starts as {} while the fetch is in flight; postSeo
    // returns null for that, and useSeo then leaves the existing tags alone
    // rather than flashing an empty title.
    //
    // A post that cannot be found is a 404, so it carries noindex - the app
    // cannot set an HTTP status, and without this an unlimited number of
    // /blog/<anything> URLs would be indexable.
    useSeo(
        notFound
            ? {
                  title: "Article not found | " + SITE_NAME,
                  description: "That article could not be found. Browse our other posts instead.",
                  canonical: ORIGIN + location.pathname,
                  noindex: true,
              }
            : postSeo(post)
    );
    useJsonLd(postSchema(post));

    // Configure axios proxy
    const axiosInstance = axios.create({ baseURL: process.env.REACT_APP_API_URL });

    useEffect(() => {
        // No extractable id, e.g. /blog/something-with-no-id-at-all.
        if (!path) {
            setNotFound(true);
            setIsLoading(false);
            return;
        }

        const getPost = async () => {
            setIsLoading(true);

            let res;
            try {
                res = await axiosInstance.get(`/posts/${path}`);
            } catch (e) {
                setNotFound(true);
                setIsLoading(false);
                return;
            }

            // findById returns null for a well-formed id that no longer
            // exists, and the API sends that back as a 200. Without this the
            // render below shows a blank title and body forever.
            if (!res || !res.data || !res.data._id) {
                setNotFound(true);
                setIsLoading(false);
                return;
            }

            setPost(res.data);
            setIsLoading(false);
        };
        getPost();
    }, [path]);

    /*
     * Tidy the address bar to the canonical slug+id form once the post is
     * known - the same pattern SingleProperty.js uses. `replace` rather than
     * `push`, so Back does not bounce the visitor between the two forms.
     *
     * This is a client-side redirect, not a 301 - the app cannot set an HTTP
     * status. The canonical tag above is what actually consolidates the two
     * forms for a search engine; this is for humans and a tidy address bar.
     */
    useEffect(() => {
        if (!post || !post._id) return;
        const canonical = postPath(post);
        if (location.pathname !== canonical) {
            history.replace(canonical);
        }
    }, [post, location.pathname, history]);

    return (
        <>
            {notFound ? (
                /*
                 * Rendered inline rather than as <NotFound />, for the same
                 * reason as SingleProperty.js: the page around this
                 * component already supplies the NavBar and Footer.
                 */
                <Container style={{ padding: "4rem 2rem 5rem" }}>
                    <Title>We can't find that article</Title>
                    <p>It may have been unpublished, or the address may be mistyped.</p>
                    <Link to="/blog">
                        <Button bright type="button">
                            Back to Blog List
                        </Button>
                    </Link>
                </Container>
            ) : isLoading ? (
                <LoaderWrapper>
                    <Loader type="Puff" color="#A2DEF9" height={60} width={60} />
                </LoaderWrapper>
            ) : (
                <Container style={{ marginBottom: "4rem" }}>
                    <ArticleWrap>
                        {post.image && (
                            /*
                             * Above the title, and only when one exists - the
                             * 19 existing posts have no image and must keep
                             * rendering exactly as they do now.
                             *
                             * The alt is the post title. It describes what the
                             * image illustrates rather than what it depicts,
                             * which is the most that can be said truthfully
                             * without a separate caption field; og:image:alt
                             * already uses the title for the same reason.
                             */
                            <FeaturedImage src={post.image} alt={post.title} />
                        )}
                        <Title>{post.title}</Title>
                        {post.videoUrl && extractYouTubeId(post.videoUrl) && (
                            <VideoWrap>
                                <VideoEmbed
                                    src={youtubeEmbedUrl(extractYouTubeId(post.videoUrl))}
                                    title={post.title}
                                    loading="lazy"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                />
                            </VideoWrap>
                        )}
                        <Body>
                            <div dangerouslySetInnerHTML={{ __html: post.content }} />
                        </Body>
                    </ArticleWrap>

                    <BackButton primary onClick={(e) => history.push("/blog")}>
                        Back to Blog List
                    </BackButton>
                </Container>
            )}
        </>
    );
}
